import { z } from 'zod';
export const Model3DExpressionEntrySchema = z.object({
    id: z.string().min(1).max(40),
    label: z.string().max(40).default(''),
    url: z.string().nullable(),
});
export const Model3DMorphBindSchema = z.object({
    meshPath: z.string().min(1).max(160).optional(),
    blendShape: z.string().min(1).max(160).optional(),
    blendShapeIndex: z.number().int().min(0).max(4096).optional(),
    weight: z.number().min(0).max(1).default(1),
}).refine((bind) => bind.blendShape !== undefined || bind.blendShapeIndex !== undefined, {
    message: 'blendShape or blendShapeIndex required',
});
export const Model3DMorphExpressionSchema = z.object({
    id: z.string().min(1).max(40),
    label: z.string().max(80).default(''),
    category: z.string().max(40).optional(),
    aliases: z.array(z.string().max(40)).max(16).default([]),
    binds: z.array(Model3DMorphBindSchema).max(64).default([]),
});
export const Model3DMorphPackSchema = z.object({
    version: z.string().max(20).optional(),
    characterName: z.string().max(120).optional(),
    expressions: z.array(Model3DMorphExpressionSchema).max(128).default([]),
});
function expressionIdFromName(name) {
    const ascii = name
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9_-]/g, '')
        .slice(0, 40);
    return ascii || 'expression';
}
/**
 * Accept the standalone viewer's `*.expressions.json` format and the
 * platform's canonical `morphPack` shape, returning a schema-validated
 * pack. The viewer format uses `presets[].binds[]` with
 * meshPath/blendShape/blendShapeIndex/weight, so it maps 1:1 to the
 * runtime's morph-target writes.
 */
export function normalizeModel3DMorphPack(input) {
    if (typeof input !== 'object' || input === null)
        return null;
    const raw = input;
    const sourceExpressions = Array.isArray(raw.expressions)
        ? raw.expressions
        : Array.isArray(raw.presets)
            ? raw.presets
            : [];
    const expressions = sourceExpressions
        .map((entry) => {
        if (typeof entry !== 'object' || entry === null)
            return null;
        const src = entry;
        const name = typeof src.name === 'string'
            ? src.name
            : typeof src.type === 'string'
                ? src.type
                : 'Expression';
        const rawBinds = Array.isArray(src.binds) ? src.binds : [];
        return {
            id: typeof src.id === 'string' && src.id.trim()
                ? expressionIdFromName(src.id)
                : expressionIdFromName(name),
            label: name.slice(0, 80),
            category: typeof src.category === 'string' ? src.category : undefined,
            aliases: Array.isArray(src.aliases) ? src.aliases : [],
            binds: rawBinds,
        };
    })
        .filter(Boolean);
    const parsed = Model3DMorphPackSchema.safeParse({
        version: typeof raw.version === 'string' ? raw.version : undefined,
        characterName: typeof raw.characterName === 'string' ? raw.characterName : undefined,
        expressions,
    });
    if (!parsed.success)
        return null;
    return {
        ...parsed.data,
        expressions: parsed.data.expressions.filter((e) => e.binds.length > 0 || e.id === 'Neutral'),
    };
}
/**
 * Canonical v2 filenames for the mandatory texture slots inside a
 * pack's `Texture/` subdirectory. Single source of truth: the
 * folder-pack auto-discovery loader, the admin-ui editor, and tests
 * all import from here so renames propagate everywhere.
 */
export const MANDATORY_PACK_FILENAMES = {
    base: 'Base.png',
    eyeMask: 'Eye_Mask.png',
    mouthMask: 'MouthMask.png',
    eyeNormal: 'Eye_normal.png',
    eyeBlink: 'Eye_blink.png',
    visemeA: 'Mouse_A.png',
    visemeE: 'Mouse_E.png',
    visemeI: 'Mouse_I.png',
    visemeO: 'Mouse_O.png',
    visemeU: 'Mouse_U.png',
};
/**
 * Recognised optional eye expression IDs. Filename convention is
 * `Eye_<id>.png`. The auto-discovery loader probes each one; absent
 * files silently omit the corresponding expression from the pack.
 */
export const KNOWN_OPTIONAL_EYE_EXPRESSION_IDS = [
    'smile',
    'happy',
    'cry',
    'angry',
    'kira',
    'shy',
    'surprised',
];
/**
 * Recognised optional mouth expression IDs. Filename convention is
 * `Mouse_<id>.png`. These are static mood mouths (distinct from the
 * vowel viseme mouths Mouse_A..U). Absent files are silently omitted.
 */
export const KNOWN_OPTIONAL_MOUTH_EXPRESSION_IDS = [
    'shock',
    'unhappy',
    'smile',
    'angry',
];
/**
 * Tolerant migration shim: legacy `closeEye` aliases to `eyeBlink`,
 * missing `eyeNormal` is derived from a `'Nature'` expression URL or
 * falls back to `base`, missing `expressions` defaults to []. We emit
 * one console.info per migrated field so we can monitor adoption in
 * the wild without spamming.
 */
function migrateLegacyPack(input) {
    if (typeof input !== 'object' || input === null)
        return input;
    const obj = input;
    const out = { ...obj };
    if (typeof obj.closeEye === 'string' && typeof obj.eyeBlink !== 'string') {
        out.eyeBlink = obj.closeEye;
        delete out.closeEye;
        console.info('[model3d-pack] migrated closeEye → eyeBlink');
    }
    if (typeof out.eyeNormal !== 'string') {
        const expressions = Array.isArray(obj.expressions) ? obj.expressions : [];
        const nature = expressions.find((e) => e?.id === 'Nature');
        if (nature && typeof nature.url === 'string') {
            out.eyeNormal = nature.url;
            console.info("[model3d-pack] migrated 'Nature' expression → eyeNormal");
        }
        else if (typeof obj.base === 'string') {
            out.eyeNormal = obj.base;
            console.info('[model3d-pack] migrated base → eyeNormal (fallback)');
        }
    }
    if (out.expressions === undefined) {
        out.expressions = [];
    }
    return out;
}
export const Model3DTexturePackSchema = z.preprocess(migrateLegacyPack, z.object({
    // Every texture slot is optional. The operator can save a pack
    // with just the slots they have uploaded so far; missing slots
    // fall back to a 1×1 transparent texture at runtime so the shader
    // layer renders as a no-op overlay. `base` was previously required
    // but that blocked first-upload-of-non-base-slot from persisting.
    base: z.string().min(1).nullable().optional(),
    eyeMask: z.string().min(1).nullable().optional(),
    eyeNormal: z.string().min(1).nullable().optional(),
    eyeBlink: z.string().min(1).nullable().optional(),
    mouthMask: z.string().min(1).nullable().optional(),
    visemes: z.object({
        idle: z.string().min(1).nullable().optional(),
        A: z.string().min(1).nullable().optional(),
        E: z.string().min(1).nullable().optional(),
        I: z.string().min(1).nullable().optional(),
        O: z.string().min(1).nullable().optional(),
        U: z.string().min(1).nullable().optional(),
    }).default({}),
    expressions: z.array(Model3DExpressionEntrySchema).default([]),
}));
const PARAM_RANGES = {
    mouthFadeSpeed: [1, 30],
    mouthOpacity: [0, 1],
    blinkMinInterval: [0.8, 8],
    blinkMaxInterval: [1, 10],
    blinkDuration: [0.08, 0.5],
    expressionFadeDuration: [0, 2],
    visemeDriftRate: [0, 20],
    mouthVisemeFadeDuration: [0, 0.5],
    mouthHoldDuration: [0, 2],
};
export const PARAMETER_IDS = Object.keys(PARAM_RANGES);
export function clampParameterValue(id, value) {
    const [lo, hi] = PARAM_RANGES[id];
    if (!Number.isFinite(value))
        return lo;
    return Math.max(lo, Math.min(hi, value));
}
export const Model3DActionSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('expression'), id: z.string().min(1) }),
    // For animation actions, `id` is the AI-visible label (e.g. '歡呼', '敬禮');
    // `clip` is the resolved AnimationClip name (e.g. 'NlaTrack.005') populated
    // server-side from the character's `clipLabels` mapping. Runtime dispatch
    // prefers `clip` and falls back to `id` for legacy callers / direct
    // clipName usage.
    z.object({ type: z.literal('defaultAnimation'), id: z.string().min(1), clip: z.string().min(1).optional() }),
    z.object({
        type: z.literal('oneShotAnimation'),
        id: z.string().min(1),
        clip: z.string().min(1).optional(),
        // Per `action-playback-loop-count`: server-side enrichment from
        // `character.oneShotLoopCounts[label]`. Absent on the wire = 1 play.
        // LLMs MUST NOT emit this field; the chat handler injects it.
        loopCount: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
    }),
    z.object({
        type: z.literal('parameter'),
        id: z.enum(PARAMETER_IDS),
        value: z.number(),
    }),
    // scene-objects-and-attachments: AI-controlled scene object visibility.
    // `id` is one of the character's ai-controlled sceneObjects[].id values.
    // The chat handler only exposes ai-controlled ids to the LLM (always-
    // visible ones are filtered out of objectGuide), so this action can't
    // hide permanent accessories.
    z.object({
        type: z.literal('object'),
        id: z.string().min(1).max(64),
        visible: z.boolean(),
    }),
]);
export const Model3DActionPlanSchema = z.object({
    text: z.string(),
    actions: z.array(Model3DActionSchema).default([]),
});
const OL_ROOT = '/samples/OL/Texture';
export const OL_SAMPLE_MODEL_URL = '/samples/OL/OL.glb';
/**
 * OL keeps its legacy filenames on disk (`.jpg` / `.JPEG` for the base
 * + visemes, `Close.png` for eye-blink, no dedicated `Eye_normal`). The
 * folder-pack auto-discovery loader is for v2-convention packs only;
 * OL works through this explicit constant. `eyeNormal` falls back to
 * the model's base texture (matches the migration shim's "no Nature
 * expression" path) — OL's UV layout treats the base face as the
 * resting eye art.
 */
export const OL_SAMPLE_PACK = {
    base: `${OL_ROOT}/base.JPEG`,
    eyeMask: `${OL_ROOT}/EyeMask.png`,
    eyeNormal: `${OL_ROOT}/base.JPEG`,
    eyeBlink: `${OL_ROOT}/Close.png`,
    mouthMask: `${OL_ROOT}/MouthMask.png`,
    visemes: {
        idle: `${OL_ROOT}/base.JPEG`,
        A: `${OL_ROOT}/A.jpg`,
        E: `${OL_ROOT}/E.jpg`,
        I: `${OL_ROOT}/I.jpg`,
        O: `${OL_ROOT}/O.jpg`,
        U: `${OL_ROOT}/U.jpg`,
    },
    expressions: [
        { id: 'Happy', label: 'Happy', url: `${OL_ROOT}/Happy.png` },
        { id: 'Angry', label: 'Angry', url: `${OL_ROOT}/Angry.png` },
        { id: 'Cry', label: 'Cry', url: `${OL_ROOT}/Cry.png` },
        { id: 'crcry', label: 'crcry', url: `${OL_ROOT}/crcry.png` },
    ],
};
const MALFEUERA_ROOT = '/samples/Malfeuera/Texture';
export const MALFEUERA_SAMPLE_MODEL_URL = '/samples/Malfeuera/Malfeuera.glb';
export const MALFEUERA_SAMPLE_PACK = {
    base: `${MALFEUERA_ROOT}/Base.png`,
    eyeMask: `${MALFEUERA_ROOT}/Eye_Mask.png`,
    eyeNormal: `${MALFEUERA_ROOT}/Eye_normal.png`,
    eyeBlink: `${MALFEUERA_ROOT}/Eye_blink.png`,
    mouthMask: `${MALFEUERA_ROOT}/MouthMask.png`,
    visemes: {
        idle: `${MALFEUERA_ROOT}/Base.png`,
        A: `${MALFEUERA_ROOT}/Mouse_A.png`,
        E: `${MALFEUERA_ROOT}/Mouse_E.png`,
        I: `${MALFEUERA_ROOT}/Mouse_I.png`,
        O: `${MALFEUERA_ROOT}/Mouse_O.png`,
        U: `${MALFEUERA_ROOT}/Mouse_U.png`,
    },
    expressions: [
        { id: 'smile', label: 'Smile', url: `${MALFEUERA_ROOT}/Eye_smile.png` },
        { id: 'cry', label: 'Cry', url: `${MALFEUERA_ROOT}/Eye_cry.png` },
        { id: 'kira', label: 'Kira', url: `${MALFEUERA_ROOT}/Eye_kira.png` },
        { id: 'shock', label: 'Shock', url: `${MALFEUERA_ROOT}/Mouse_shock.png` },
        { id: 'unhappy', label: 'Unhappy', url: `${MALFEUERA_ROOT}/Mouse_unhappy.png` },
    ],
};
